import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { z } from 'zod';
import { VARIANT_IDS } from '@/engine/variants';
import type { VariantId } from '@/engine/types';

const variantIdSchema = z.enum(VARIANT_IDS as [VariantId, ...VariantId[]]);

export const settingsSchema = z.object({
  theme: z
    .enum(['neon', 'felt', 'midnight', 'paper', 'synthwave', 'backyard'])
    .default('neon'),
  diceTheme: z
    .enum(['neon', 'ivory', 'casino', 'wood', 'gem', 'ink', 'chrome'])
    .default('neon'),
  rollAnimation: z
    .enum(['tumble', 'spin', 'shake', 'instant'])
    .default('tumble'),
  rollSpeed: z.enum(['slow', 'normal', 'fast']).default('normal'),
  cup: z.enum(['leather', 'chrome', 'neon', 'plastic', 'none']).default('neon'),
  motion: z.enum(['system', 'full', 'reduced']).default('system'),
  sound: z.boolean().default(true),
  volume: z.number().min(0).max(1).default(0.7),
  haptics: z.boolean().default(true),
  confetti: z.boolean().default(true),
  /** Ambient drifting dice behind every page. */
  backgroundDice: z.boolean().default(true),
  /** Show what each open box would score for the current dice. */
  showPreviews: z.boolean().default(true),
  /** Display dice sorted by value (holds still track physical dice). */
  sortDice: z.boolean().default(false),
  /** Name used for this device's default player. */
  playerName: z.string().max(24).default('You'),
  defaultVariant: variantIdSchema.default('classic'),
  /** Milliseconds AI pauses between visible steps (0 = instant). */
  aiPace: z.number().min(0).max(3000).default(900),
});

export type Settings = z.infer<typeof settingsSchema>;
export const DEFAULT_SETTINGS: Settings = settingsSchema.parse({});

interface SettingsStore extends Settings {
  set: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  update: (patch: Partial<Settings>) => void;
  reset: () => void;
}

export const useSettings = create<SettingsStore>()(
  persist(
    set => ({
      ...DEFAULT_SETTINGS,
      set: (key, value) => set({ [key]: value } as Partial<Settings>),
      update: patch => set(patch),
      reset: () => set(DEFAULT_SETTINGS),
    }),
    {
      name: 'yazzi.settings.v1',
      partialize: state => {
        const { set: _s, update: _u, reset: _r, ...rest } = state;
        return rest;
      },
      merge: (persisted, current) => {
        const parsed = settingsSchema.safeParse(persisted);
        return { ...current, ...(parsed.success ? parsed.data : {}) };
      },
    }
  )
);

/** Resolved reduced-motion preference. */
export function prefersReducedMotion(motion: Settings['motion']): boolean {
  if (motion === 'reduced') return true;
  if (motion === 'full') return false;
  return typeof window !== 'undefined'
    ? (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false)
    : false;
}
